import React, { useEffect } from 'react';
import { useMarketAggregates } from '../contexts/MarketAggregatesContext';
import { VariableType } from '../types/dashboard.types';

interface WeightTypeSelectorProps {
  selectedVariable: VariableType;
}

const WeightTypeSelector: React.FC<WeightTypeSelectorProps> = ({ selectedVariable }) => {
  const { state, setWeightType, fetchAvailableZones } = useMarketAggregates();

  // Get display label for current weighting
  const getWeightingLabel = (): string => {
    switch (state.weightType) {
      case 'population':
        return 'Population';
      case 'wind_capacity':
        return 'Capacity (Wind)';
      case 'solar_capacity':
        return 'Capacity (Solar)';
      default:
        return 'Population';
    }
  };

  // Auto-switch to population when temperature is selected and capacity was active
  useEffect(() => {
    if (selectedVariable === 'temperature' && state.weightType !== 'population') {
      setWeightType('population');
    }
  }, [selectedVariable, state.weightType, setWeightType]);

  // Auto-switch to appropriate capacity type when wind or solar is selected
  useEffect(() => {
    if (selectedVariable === 'wind' || selectedVariable === 'solar') {
      const expectedCapacityType = selectedVariable === 'wind' ? 'wind_capacity' : 'solar_capacity';

      // Switch to the correct capacity type if:
      // 1. Currently using population (switch from population to capacity)
      // 2. Currently using wrong capacity type (switch between wind_capacity and solar_capacity)
      if (state.weightType === 'population' || state.weightType !== expectedCapacityType) {
        setWeightType(expectedCapacityType);
      }
    }
  }, [selectedVariable, state.weightType, setWeightType]);

  // Fetch available zones whenever weight type changes (includes initial mount)
  useEffect(() => {
    fetchAvailableZones(state.weightType);
  }, [state.weightType, fetchAvailableZones]);

  return (
    <div className="flex items-center gap-2">
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
        Weighting
      </label>
      <div className="px-3 py-1 text-xs font-medium bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md border border-gray-200 dark:border-gray-600 whitespace-nowrap">
        {getWeightingLabel()}
      </div>
    </div>
  );
};

export default WeightTypeSelector;
